<template>
  <div class="mobile-splash" :class="{ 'is-leaving': isLeaving }">
    <!-- 배경 -->
    <div class="splash-bg"></div>

    <!-- 워터마크 -->
    <img src="@/assets/logo/logo_symbol.png" alt="" class="splash-watermark" />

    <!-- 콘텐츠 -->
    <div class="splash-content">
      <p class="sub-title">프리미엄 라이프의 새로운 기준</p>
      <h1 class="main-title">
        <span class="title-brand">테크노</span>
        <span class="title-central">레이원시티</span>
      </h1>
      <div class="divider"></div>
      <p class="slogan">TECHNO RAYONE CITY</p>
    </div>

    <!-- 로딩 퍼센트 -->
    <div class="loading-area">
      <div class="percent-display">
        <span class="percent-number">{{ displayPercent }}</span>
        <span class="percent-sign">%</span>
      </div>
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: displayPercent + '%' }"></div>
      </div>
      <p class="loading-text">LOADING</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MobileSplashScreen',
  data() {
    return {
      percent: 0,
      displayPercent: 0,
      isLeaving: false,
      animFrame: null
    }
  },
  mounted() {
    document.body.style.overflow = 'hidden'
    this.preloadImages()
  },
  beforeUnmount() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame)
  },
  methods: {
    preloadImages() {
      let loaded = 0
      let total = 0
      const images = []

      try {
        const requireImages = require.context(
          '@/assets/mobile/',
          false,
          /^\.\/home\d+\.(jpg|jpeg|png|gif|webp)$/i
        )
        const keys = requireImages.keys()
        total = keys.length

        if (total === 0) {
          this.finishLoading()
          return
        }

        keys.forEach(key => {
          const src = requireImages(key)
          const img = new Image()
          img.onload = img.onerror = () => {
            loaded++
            this.percent = Math.round((loaded / total) * 100)
            this.animatePercent()
            if (loaded >= total) {
              this.finishLoading()
            }
          }
          img.src = src
          images.push(img)
        })
      } catch (e) {
        this.finishLoading()
      }

      // 안전장치: 15초 후 강제 종료
      this.safetyTimer = setTimeout(() => {
        if (!this.isLeaving) {
          this.percent = 100
          this.displayPercent = 100
          this.finishLoading()
        }
      }, 15000)
    },

    animatePercent() {
      if (this.animFrame) cancelAnimationFrame(this.animFrame)

      const animate = () => {
        if (this.displayPercent < this.percent) {
          this.displayPercent = Math.min(this.displayPercent + 1, this.percent)
          this.animFrame = requestAnimationFrame(animate)
        }
      }
      this.animFrame = requestAnimationFrame(animate)
    },

    finishLoading() {
      if (this.safetyTimer) clearTimeout(this.safetyTimer)

      // 100%까지 애니메이션 완료 후 닫기
      const waitForDisplay = () => {
        if (this.displayPercent >= 100) {
          setTimeout(() => this.startClose(), 400)
        } else {
          this.displayPercent = Math.min(this.displayPercent + 1, 100)
          requestAnimationFrame(waitForDisplay)
        }
      }
      this.percent = 100
      requestAnimationFrame(waitForDisplay)
    },

    startClose() {
      if (this.isLeaving) return
      this.isLeaving = true

      setTimeout(() => {
        document.body.style.overflow = ''
        this.$emit('close')
      }, 800)
    }
  }
}
</script>

<style scoped>
.mobile-splash {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.mobile-splash.is-leaving {
  opacity: 0;
  transition: opacity 0.8s ease;
}

/* 배경 */
.splash-bg {
  position: absolute;
  inset: 0;
  background: var(--rayone-primary);
  z-index: 0;
}

/* 워터마크 */
.splash-watermark {
  position: absolute;
  right: -15%;
  bottom: -5%;
  height: 50vh;
  opacity: 0;
  filter: brightness(0) invert(1);
  pointer-events: none;
  z-index: 1;
  animation: watermarkIn 1.5s ease-out 0.3s forwards;
}

@keyframes watermarkIn {
  0% { opacity: 0; transform: scale(0.95); }
  100% { opacity: 0.05; transform: scale(1); }
}

/* 콘텐츠 */
.splash-content {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 0 24px;
}

.sub-title {
  font-family: var(--font-pretendard);
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 2px;
  margin-bottom: 16px;
  opacity: 0;
  transform: translateY(20px);
  animation: fadeUp 0.8s ease-out 0.3s forwards;
}

.main-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
}

.title-brand {
  font-family: var(--font-pretendard);
  font-size: 36px;
  font-weight: 800;
  color: #fff;
  letter-spacing: 8px;
  opacity: 0;
  transform: translateY(30px);
  animation: fadeUp 1s cubic-bezier(0.16, 1, 0.3, 1) 0.5s forwards;
}

.title-central {
  font-family: var(--font-pretendard);
  font-size: 24px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 10px;
  opacity: 0;
  transform: translateY(30px);
  animation: fadeUp 1s cubic-bezier(0.16, 1, 0.3, 1) 0.7s forwards;
}

.divider {
  width: 40px;
  height: 1px;
  background: rgba(255, 255, 255, 0.4);
  margin: 0 auto 20px;
  opacity: 0;
  animation: dividerExpand 0.8s ease-out 1s forwards;
}

@keyframes dividerExpand {
  0% { width: 0; opacity: 0; }
  100% { width: 40px; opacity: 1; }
}

.slogan {
  font-family: var(--font-paperlogy);
  font-size: 11px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.35);
  letter-spacing: 4px;
  opacity: 0;
  animation: fadeUp 0.8s ease-out 1.2s forwards;
}

/* 로딩 영역 */
.loading-area {
  position: absolute;
  bottom: 60px;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 0 40px;
  opacity: 0;
  animation: fadeUp 0.6s ease-out 0.5s forwards;
}

.percent-display {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.percent-number {
  font-family: var(--font-paperlogy);
  font-size: 48px;
  font-weight: 700;
  color: #fff;
  line-height: 1;
  min-width: 80px;
  text-align: right;
}

.percent-sign {
  font-family: var(--font-paperlogy);
  font-size: 20px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
}

.progress-bar {
  width: 100%;
  max-width: 200px;
  height: 2px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 1px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #fff;
  border-radius: 1px;
  transition: width 0.3s ease-out;
}

.loading-text {
  font-family: var(--font-paperlogy);
  font-size: 10px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.3);
  letter-spacing: 6px;
}

@keyframes fadeUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
